import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Actions } from '@src/app/shared/backend/auth/Action';
import { PermissionService } from '@src/app/shared/services/permission.service';
import { TranslateService } from '@ngx-translate/core';
import { ShipmentsService, } from '@src/app/shared/backend/clients';
import { BaseListComponent } from '@src/app/shared/components/base-list/base-list.component';
import { TableSettingService } from '@src/app/shared/services/table.setting.service';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToasterService } from '@src/app/shared/components/toaster/toaster.service';
import { ExportService } from '@src/app/shared/services/export.service';
import { FormGroup } from '@angular/forms';
import { FiltersPanelChangeEvent, FiltersPanelFieldOverrides,FiltersPanelDropdownLazyLoadEvent } from '@src/app/shared/components/filters-panel/filters-panel.models';
import { applyFiltersPanelChange } from '@src/app/shared/components/filters-panel/filters-panel.helper';
import { FriendlyBooleanPipe } from '@app/shared/pipes/friendlyBoolean.pipe';
import { loadStoredPaginatorState, resetPaginatorStateToFirstPage } from '@app/shared/components/paginator/paginator-storage.helper';
import { buildRemoteDropdownRequest, normalizeDropdownState } from '@src/app/shared/components/filters-panel/filters-panel-dropdown.helpers';

@Component({
    selector: 'app-shipments',
    templateUrl: './shipments.component.html',
    styleUrl: './shipments.component.scss',
    host: {
        class: 'app-shipments'
    },
    standalone: false
})
export class ShipmentsComponent extends BaseListComponent implements OnInit, OnDestroy {

  private initialLoadDone = false;
  private readonly componentDestroyed$ = new Subject<void>();
  public readonly shipmentsTableName = TableSettingService.ShipmentsTableName;

  public filtersPanelFieldOverrides: FiltersPanelFieldOverrides = {};

  // Dropdown: isCancelled
  private cancelledOptions: Array<{ id: string | number; text: string }> = [];
  // Dropdown: isReceived
  private receivedOptions: Array<{ id: string | number; text: string }> = [];

  exports: boolean = false;
  allowCreateShipment: boolean = false;
  currentDate: Date = new Date();
  displayExportModalbox: boolean = false;

  constructor(
    private router: Router,
    public permissionService: PermissionService,
    public override tableSettingService: TableSettingService,
    private translateService: TranslateService,
    private toasterService: ToasterService,
    private exportService: ExportService,
    private shipmentsService: ShipmentsService
  ) {
    super();
  }

  ngOnInit() {
    this.init();
  }

  private async init(): Promise<void> {
    this.restorePaginatorStateFromStorage();

    this.exports = await this.permissionService.hasActionPermissions(Actions.export_shipments);
    this.allowCreateShipment = await this.permissionService.hasActionPermissions(Actions.create_shipments);

    // i18n propagation on lang change
    this.rebuildCancelledOptions();
    this.rebuildReceivedOptions();

    this.translateService.onLangChange
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(() => {
        this.rebuildCancelledOptions();
        this.rebuildReceivedOptions();
        this.rebuildFiltersPanelOverrides();

        // Update sort labels without resetting the current sort selection
        this.rebuildSortPanelColumnLabels();
      });

    await this.tableSettingService.init();
    await super.setCache(this.shipmentsTableName);

    // default sort for stable pagination + 18n fn labelResolver (sort dropdown options)
    this.initSortPanel({
      defaultSort: { key: 'createdOn', direction: 'DESC' },
      labelResolver: (col) => {
        const translationKey = `TABLE.HEADER.${col.header}`;
        const translated = this.translateService.instant(translationKey);

        if (translated && translated !== translationKey) {
          return translated;
        }

        return col.header;
      }
    });

    // Build dropdown overrides (local dropdown)
    this.rebuildSortPanelColumnLabels();
    this.rebuildFiltersPanelOverrides();

    // Fallback: if the panel doesn't restore anything, load unfiltered!!
    setTimeout(async () => {
      if (!this.initialLoadDone) {
        this.initialLoadDone = true;
        await this.get();
      }
    });
  }

  override async get() {
    this.filter.pageNumber = this.pageNumber;
    this.filter.pageSize = this.pageSize;

    this.applySortToFilterRequest();

    this.page = await firstValueFrom(this.shipmentsService.apiShipmentsFilterTablePost(this.filter));
    this.data = this.page.results;
  }

  override onPageChange(event: any): void {
    this.initialLoadDone = true;
    super.onPageChange(event);
  }

  protected override onCriteriaResetRequested(): void {
    this.resetPaginatorStorage();
  }

  private resetPaginatorStorage(): void {
    resetPaginatorStateToFirstPage({
      tableName: this.shipmentsTableName,
      pageSize: this.pageSize,
    });
  }

  private restorePaginatorStateFromStorage(): void {
    const stored = loadStoredPaginatorState(this.shipmentsTableName);

    if (!stored) {
      return;
    }

    const storedPageNumber = stored.valuesByFieldKey.pageNumber;
    const storedPageSize = stored.valuesByFieldKey.pageSize;

    this.pageNumber = storedPageNumber;
    this.pageSize = storedPageSize;

    this.filter.pageNumber = storedPageNumber;
    this.filter.pageSize = storedPageSize;
  }

  private rebuildReceivedOptions() {
    const pipe = new FriendlyBooleanPipe(this.translateService);
    this.receivedOptions = pipe.values;
  }

  async onFiltersPanelChange(evt: FiltersPanelChangeEvent): Promise<void> {
    // Important: pass all table cols (not only visible ones) to prevent hidden col filters from affecting results
    const currentFilters = this.filter.filters as any;

    const nextFilters = applyFiltersPanelChange(
      evt,
      currentFilters,
      this.allTableColumns
    );

    if (!nextFilters) {
      return;
    }

    this.filter.filters = nextFilters as any;

    const source = evt.source;
    const isApplyAction = source === 'apply';
    const isClearAction = source === 'clear';
    const shouldResetPaging = isApplyAction || isClearAction;

    if (shouldResetPaging) {
      const firstPageNumber = 1;

      this.pageNumber = firstPageNumber;
      this.filter.pageNumber = firstPageNumber;

      this.onCriteriaResetRequested();
    }

    this.initialLoadDone = true;
    await this.get();
  }


  private rebuildCancelledOptions() {
    const pipe = new FriendlyBooleanPipe(this.translateService);
    this.cancelledOptions = pipe.values;
  }

  private rebuildFiltersPanelOverrides(): void {
    this.filtersPanelFieldOverrides = {
      isCancelled: {
        fieldLabel: 'TABLE.HEADER.Cancelled',
        isMultiselect: true,

        options: this.cancelledOptions ?? [],
        optionLabel: 'text',
        optionValue: 'id',

        placeholder: 'Select...',
        searchMode: 'local',
        showSearchbox: false,
        showClear: true,
        compactUi: true
      },
      isReceived: {
        fieldLabel: 'TABLE.HEADER.Received',
        isMultiselect: true,

        options: this.receivedOptions ?? [],
        optionLabel: 'text',
        optionValue: 'id',

        placeholder: 'Select...',
        searchMode: 'local',
        showSearchbox: false,
        showClear: true,
        compactUi: true
      },
    };
  }

  private rebuildSortPanelColumnLabels(): void {
    const visibleColumns = Array.isArray(this.tableColumns) ? this.tableColumns : [];

    // Update sort labels without resetting the current sort selection
    const nextSortableColumns = visibleColumns
      .filter((col: any) => {
        const field = String(col?.field ?? '').trim();
        return (
          field.length > 0 &&
          field.toLowerCase() !== 'actions' &&
          col?.sortable !== false
        );
      })
      .map((col: any) => {
        const translationKey = `TABLE.HEADER.${col.header}`;
        const translated = this.translateService.instant(translationKey);

        let label;
        if (translated && translated !== translationKey) {
          label = translated;
        } else {
          label = col.header;
        }

        return {
          key: col.field,
          label
        };
      });

    this.sortableColumns = nextSortableColumns;

    // clear active sort if points to col that is not available due to system settings update
    const allowedKeys = new Set(nextSortableColumns.map(col => col.key));
    if (this.sortPanelSort?.key && !allowedKeys.has(this.sortPanelSort.key)) {
      this.onSortPanelChange(null);
    }
  }

  goToShipmentDetails(event: any, shipmentId: string) {
    this.router.navigate([`/shipments/${shipmentId}`]);
  }

  goToCreateShipmentForm() {
    this.router.navigate(['/shipments/add']);
  }

  onOpenExportModalbox() {
    this.displayExportModalbox = true;
  }

  handleExportSubmit(form: FormGroup) {
    const range = form.value.dateRange;
    const fileName = form.value.exportName;
    const fileType = form.value.format;

    this.exportService.exportData(
      this.filter,
      [range?.[0], range?.[1] ?? new Date()],
      this.shipmentsService.apiShipmentsExportPost.bind(this.shipmentsService),
      {
        fileName,
        fileType,
        onSuccess: () => {
          const title = this.translateService.instant('EXPORT_SETTINGS.TOAST_SUCCESS_TITLE');
          const message = this.translateService.instant('EXPORT_SETTINGS.TOAST_SUCCESS_MESSAGE');
          this.toasterService.show('success', title, message);
        },
        onError: (error) => {
          const title = this.translateService.instant('EXPORT_SETTINGS.TOAST_ERROR_TITLE');
          const message = this.translateService.instant('EXPORT_SETTINGS.TOAST_ERROR_MESSAGE');
          this.toasterService.show('error', title, message);
          console.error('Export error:', error);
        }
      }
    );
  }

  ngOnDestroy() {
    this.componentDestroyed$.next();
    this.componentDestroyed$.complete();
  }
}
